import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import MessageRoundedIcon from '@mui/icons-material/MessageRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useTranslation } from 'react-i18next';
import { ChatAsset, ChatLibraryEntry } from '../../models/Chat';
import {
  formatChatTimestamp,
  isChatImageAsset,
  chatLibraryEntryAsAsset,
} from '../../utils/chat';
import ChatAssetAttachment from './ChatAssetAttachment';
import ChatSaveToLibraryButton from './ChatSaveToLibraryButton';
import { chatPanelScrollSx } from './chatLayout';

interface ChatLibraryPanelProps {
  entries: ChatLibraryEntry[];
  assetsById: ReadonlyMap<string, ChatAsset>;
  loading: boolean;
  error: string | null;
  removingEntryId: string | null;
  locale: string;
  onRemoveEntry: (entry: ChatLibraryEntry) => void;
}

function LibraryEntryPreview({
  entry,
  asset,
}: {
  entry: ChatLibraryEntry;
  asset: ChatAsset | null;
}) {
  if (entry.sourceType === 'MESSAGE') {
    return <MessageRoundedIcon sx={{ color: 'primary.light', flexShrink: 0 }} />;
  }

  if (!asset) {
    return <InsertDriveFileOutlinedIcon sx={{ color: 'primary.light', flexShrink: 0 }} />;
  }

  if (asset.kind === 'LINK') {
    return <LinkRoundedIcon sx={{ color: 'primary.light', flexShrink: 0 }} />;
  }

  if (isChatImageAsset(asset)) {
    return <ChatAssetAttachment asset={asset} variant="list" />;
  }

  return <InsertDriveFileOutlinedIcon sx={{ color: 'primary.light', flexShrink: 0 }} />;
}

const ChatLibraryPanel: React.FC<ChatLibraryPanelProps> = ({
  entries,
  assetsById,
  loading,
  error,
  removingEntryId,
  locale,
  onRemoveEntry,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={chatPanelScrollSx}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {entries.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('chat.library.empty', 'Nothing saved to the library yet.')}
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {entries.map((entry) => {
              const isMessage = entry.sourceType === 'MESSAGE';
              const isLink = entry.assetKind === 'LINK';
              const asset = chatLibraryEntryAsAsset(entry, assetsById);

              return (
                <Stack
                  key={entry.id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <LibraryEntryPreview entry={entry} asset={asset} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {entry.url ? (
                      <Link
                        href={entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                        sx={{ fontWeight: 600, display: 'block', wordBreak: 'break-word' }}
                      >
                        {entry.title || entry.url}
                      </Link>
                    ) : (
                      <Typography variant="body2" fontWeight={600}>
                        {entry.title || t('chat.library.untitled', 'Saved item')}
                      </Typography>
                    )}
                    {entry.body && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {entry.body}
                      </Typography>
                    )}
                    {entry.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                        {entry.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          isMessage
                            ? t('chat.library.kindMessage', 'Message')
                            : isLink
                              ? t('chat.library.kindLink', 'Link')
                              : t('chat.library.kindFile', 'File')
                        }
                      />
                      {entry.sourceAuthorName && (
                        <Typography variant="caption" color="text.secondary">
                          {entry.sourceAuthorName}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatChatTimestamp(entry.createdAt, locale)}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                    {entry.url && (
                      <IconButton
                        size="small"
                        component="a"
                        href={entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('chat.files.openInNewTab', 'Open in new tab')}
                      >
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                    )}
                    <ChatSaveToLibraryButton
                      saved
                      saving={removingEntryId === entry.id}
                      onClick={() => onRemoveEntry(entry)}
                    />
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default ChatLibraryPanel;
